import {ApolloClient as Client, split} from '@apollo/client/core';
import {InMemoryCache} from '@apollo/client/cache';
import {getMainDefinition} from '@apollo/client/utilities';
import {BatchHttpLink} from '@apollo/client/link/batch-http';
import {createPersistedQueryLink} from '@apollo/client/link/persisted-queries';
import {createUploadLink as CreateUploadLink} from 'apollo-upload-client';
import fetch from 'cross-fetch';
import {sha256} from 'crypto-hash';

export const CreateApolloClient = ({
  clientId = 'defaultClient',
  httpEndpoint,
  wsEndpoint = null,
  tokenName = 'apollo-token',
  persisting = false,
  batching = false,
  ssr = false,
  websocketsOnly = false,
  link = null,
  defaultHttpLink = true,
  httpLinkOptions = {},
  cache = null,
  inMemoryCacheOptions = {},
  apollo = null,
  clientState = null,
  onCacheInit = undefined,
  resolvers = undefined,
  typeDefs = undefined,
}) => {
  let stateLink;
  const disableHttp = websocketsOnly && !ssr && wsEndpoint;

  if (!cache) {
    cache = new InMemoryCache(inMemoryCacheOptions);
  }

  if (!disableHttp) {
    const httpLink = new CreateUploadLink({
      uri: httpEndpoint,
      fetch,
      ...httpLinkOptions,
    });

    if (!link) {
      link = httpLink;
    } else if (defaultHttpLink) {
      link = link.concat(httpLink);
    }

    // APQ
    let persistingOpts = {
      sha256,
      useGETForHashedQueries: true,
    };
    if (typeof persisting === 'object' && persisting !== null) {
      persistingOpts = {
        ...persistingOpts,
        ...persisting,
      };
      persisting = true;
    }
    if (persisting) {
      link = createPersistedQueryLink(persistingOpts).concat(link);
    }

    const batchingOpts = {
      options: {
        uri: httpEndpoint,
        fetch,
        ...httpLinkOptions,
      },
      original: {
        options: {
          fetch,
        },
        splitCondition: null,
        fallbackLink: null,
      },
    };

    if (typeof batching === 'object' && batching !== null) {
      batchingOpts.options = {
        ...batchingOpts.options,
        ...batching.options,
      };
      batchingOpts.original = {
        ...batchingOpts.original,
        ...batching,
      };

      batching = true;
    }

    if (batching) {
      const batchLink = new BatchHttpLink(batchingOpts.options);
      link = split(
        batchingOpts.original.splitCondition || (({query}) => {
          const {kind, operation} = getMainDefinition(query);

          return kind === 'OperationDefinition' && ['mutation', 'subscription'].includes(operation);
        }),
        batchingOpts.original.fallbackLink || link,
        batchLink,
      );
    }
  }

  if (!ssr) {
    if (process.client && window.__NUXT__.apollo) {
      cache.restore(window.__NUXT__.apollo);
    }

    if (wsEndpoint) {
      throw new Error('[Apollo module] WS support not added.');
    }
  }

  if (clientState) {
    throw new Error('[Apollo module] clientState is deprecated.');
  }

  const apolloClient = new Client({
    link,
    cache,
    ssrMode: ssr,
    ...(
      ssr
        ? {}
        : {
          // This will temporary disable query force-fetching
          ssrForceFetchDelay: 100,
          // Apollo devtools
          connectToDevTools: process.env.NODE_ENV !== 'production',
        }
    ),
    resolvers,
    typeDefs,
    ...apollo,
  });

  if (stateLink) {
    apolloClient.onResetStore(stateLink.writeDefaults);
  }

  if (onCacheInit) {
    onCacheInit(cache);
    apolloClient.onResetStore(() => onCacheInit(cache));
  }

  return {
    apolloClient,
  };
};

export const handleSmartQuery = async (ctx, key, query, provider) => {
  try {
    if ((query.skip instanceof Boolean && query.skip) || (query.skip instanceof Function && query.skip.call(ctx))) {
      return;
    }

    const watchedQuery = provider.defaultClient.watchQuery({
      ...query,
      // Check if we have variables and if the variables key is a function execute it. If it isn't a function just return it and if we dont have variables use an empty object
      variables: query.variables ? query.variables instanceof Function ? query.variables.call(ctx) : query.variables : {},
    });

    const result = await watchedQuery.result();

    if (query.result) {
      query.result.call(ctx, result);
    }
  } catch (error) {
    if (query.error) {
      query.error.call(ctx, error, ctx, key, 'query');
    }
  }
};
